import React from "react";
import { Popover } from "antd";
import { Num } from "../../../../components/shared-components/purchase";

/**
 * Onder elke regel in stap 2: één dunne balk en één rij getallen, zodat je bij
 * het invullen van het aantal ziet wat er al is en wat er al aankomt.
 *
 * De balk loopt van donker (er nu al) naar licht (nog op komst) en eindigt in
 * een gestreept stuk: het aantal dat je op deze regel invult, dat dus
 * meegroeit terwijl je typt. Het zwarte streepje is wat er in de gekozen
 * verkoopperiode verkocht is. Komt de balk voorbij het streepje, dan heb je
 * straks meer dan je in zo'n periode verkoopt.
 *
 * De woorden staan één keer, in `LineStockLegend` boven de tabel, niet per
 * regel. De productkolom is op een gewoon scherm zo'n 220px breed; met de
 * woorden erbij liep elke regel over vier regels tekst. De getallen staan in
 * vaste vakjes, zodat ze over de regels heen onder elkaar staan als kleine
 * kolommen. Wat per regel te veel zou zijn (welke orders, welke verzendingen)
 * zit achter hover/klik, met de woorden er wél bij.
 */
const COLORS = {
  stock: "#938370",
  inTransit: "#B8AB99",
  onOrder: "#D9D0C3",
  thisOrder: "repeating-linear-gradient(135deg, #938370 0 2px, #E9E4DC 2px 4px)",
};
const TRACK = "#F1EFEB";
const SOLD = "rgba(0, 0, 0, 0.7)";

const Swatch = ({ background }) => (
  <span
    style={{ width: 8, height: 8, borderRadius: 2, background, display: "inline-block", flex: "none", marginRight: 4 }}
  />
);

const SoldTick = () => (
  <span
    style={{ width: 2, height: 10, borderRadius: 1, background: SOLD, display: "inline-block", flex: "none", marginRight: 5 }}
  />
);

const Figure = ({ name, color, value, tick }) => (
  <span data-figure={name} style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
    {tick ? <SoldTick /> : <Swatch background={color} />}
    <Num size={12} muted={!value}>
      {value}
    </Num>
  </span>
);

const LegendEntry = ({ color, tick, children }) => (
  <span style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
    {tick ? <SoldTick /> : <Swatch background={color} />}
    {children}
  </span>
);

/** De sleutel bij de balken, één keer per tabel. */
export const LineStockLegend = () => (
  <span
    style={{
      display: "inline-flex",
      flexWrap: "wrap",
      gap: "2px 12px",
      fontSize: 12,
      fontWeight: 400,
      color: "rgba(0, 0, 0, 0.55)",
    }}
  >
    <LegendEntry color={COLORS.stock}>voorraad</LegendEntry>
    <LegendEntry color={COLORS.inTransit}>onderweg</LegendEntry>
    <LegendEntry color={COLORS.onOrder}>in bestelling</LegendEntry>
    <LegendEntry color={COLORS.thisOrder}>deze order</LegendEntry>
    <LegendEntry tick>verkocht in periode</LegendEntry>
  </span>
);

const BreakdownRow = ({ label, value, color, tick, strong, children }) => (
  <>
    <div style={{ display: "flex", alignItems: "center", gap: 16, justifyContent: "space-between" }}>
      <span style={{ display: "inline-flex", alignItems: "center", fontWeight: strong ? 500 : 400 }}>
        {tick ? <SoldTick /> : color ? <Swatch background={color} /> : null}
        {label}
      </span>
      <Num strong={strong}>{value}</Num>
    </div>
    {children}
  </>
);

const SubRow = ({ label, value }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 16, paddingLeft: 12 }}>
    <Num size={12} muted>
      {label}
    </Num>
    <Num size={12} muted>
      {value}
    </Num>
  </div>
);

const LineStockPicture = ({ picture, periodLabel, loading }) => {
  if (loading) {
    return (
      <Num size={12} muted style={{ display: "block", marginTop: 6 }}>
        Voorraad en verkoop laden…
      </Num>
    );
  }

  const { stock, inTransit, onOrder, thisOrder, sold, expected, segments, soldAt } = picture;

  const breakdown = (
    <div style={{ minWidth: 240, display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <BreakdownRow label="Voorraad" value={stock} color={COLORS.stock} />
      <BreakdownRow label="Onderweg" value={inTransit.total} color={COLORS.inTransit}>
        {inTransit.shipments.map((s) => (
          <SubRow key={s.shipmentNumber} label={s.shipmentNumber} value={s.amount} />
        ))}
      </BreakdownRow>
      <BreakdownRow label="In bestelling" value={onOrder.total} color={COLORS.onOrder}>
        {onOrder.orders.map((o) => (
          <SubRow key={o.orderNumber} label={o.orderNumber} value={`${o.amount} open`} />
        ))}
      </BreakdownRow>
      <BreakdownRow label="Deze order" value={thisOrder} color={COLORS.thisOrder} />
      <div style={{ borderTop: "1px solid #E1DFDA", margin: "4px 0" }} />
      <BreakdownRow label="Straks totaal" value={expected} strong />
      <BreakdownRow label="Verkocht in periode" value={sold} tick>
        {periodLabel ? <SubRow label={periodLabel} value="" /> : null}
      </BreakdownRow>
    </div>
  );

  return (
    <Popover trigger={["hover", "click"]} mouseEnterDelay={0.15} title="Voorraadbeeld" content={breakdown}>
      <div className="po-line-stock" style={{ marginTop: 6, maxWidth: 240, cursor: "default" }}>
        <div style={{ position: "relative", height: 6, margin: "2px 0" }}>
          <div
            style={{
              display: "flex",
              height: "100%",
              borderRadius: 3,
              overflow: "hidden",
              background: TRACK,
            }}
          >
            {segments.map((segment) => (
              <span
                key={segment.key}
                data-segment={segment.key}
                style={{ width: `${segment.width}%`, background: COLORS[segment.key], height: "100%" }}
              />
            ))}
          </div>
          {soldAt !== null ? (
            <span
              data-sold-tick
              style={{
                position: "absolute",
                left: `clamp(0px, calc(${soldAt}% - 1px), calc(100% - 2px))`,
                top: -3,
                width: 2,
                height: 12,
                borderRadius: 1,
                background: SOLD,
              }}
            />
          ) : null}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(40px, max-content))",
            gap: "0 10px",
            marginTop: 5,
          }}
        >
          <Figure name="stock" color={COLORS.stock} value={stock} />
          <Figure name="inTransit" color={COLORS.inTransit} value={inTransit.total} />
          <Figure name="onOrder" color={COLORS.onOrder} value={onOrder.total} />
          <Figure name="sold" tick value={sold} />
        </div>
      </div>
    </Popover>
  );
};

export default LineStockPicture;
